import React, { useState, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import axios from 'axios';
import Swal from 'sweetalert2';
import ComptableLayout from '../../layout/comptable-layout';
import DuoPeriodPicker from '../../components/ui/duo-period-picker';
import { APP_API_URL, APP_NAME } from '../../utils';
import formatNumber, {
  defaultFilterDates,
  DUO_PERIOD_OPTIONS,
  formatSoldQuantityLabel,
  getPeriodDateRange,
} from '../../utils/functions';

const PER_PAGE = 10;
const PERIOD_DEFAULT = DUO_PERIOD_OPTIONS.find((p) => p.value === 'ce_mois') ?? {
  label: 'Ce mois',
  value: 'ce_mois',
};
const periodOptions = [PERIOD_DEFAULT, ...DUO_PERIOD_OPTIONS.filter((p) => p.value !== 'ce_mois')];

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'ascending' ? '↑' : '↓') : '↕'}</span>
);

const formatCodeLabel = (item) => {
  const sku = (item.sku || '').trim();
  const format = (item.format || '').trim();
  if (sku && format) return `${sku} (${format})`;
  if (sku) return sku;
  if (format) return `(${format})`;
  return '—';
};

const localYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const periodToStockParams = (period, dateFrom, dateTo) => {
  const params = {};
  if (period) {
    const range = getPeriodDateRange(period);
    if (range) {
      params.startDate = localYmd(range.from);
      params.endDate = localYmd(range.to);
      return params;
    }
    const map = {
      "aujourd'hui": 'today',
      hier: 'yesterday',
      dernier_7_jours: 'this-week',
      ce_mois: 'this-month',
      this_year: 'this-year',
    };
    if (map[period]) {
      params.dateRange = map[period];
      return params;
    }
  }
  if (dateFrom) params.startDate = dateFrom;
  if (dateTo) params.endDate = dateTo;
  if (!dateFrom && !dateTo && !period) params.dateRange = 'this-month';
  return params;
};

const StockQtyCell = ({ value, row }) => (
  <div className="stock-qty-cell text-end">{formatSoldQuantityLabel(value, row)}</div>
);

export default function ComptableInventoryPage() {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'product_name', direction: 'ascending' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState({ label: 'Personnalisé', value: '' });
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    period: '',
    ...defaultFilterDates(),
  }));
  const [draftFilters, setDraftFilters] = useState(() => defaultFilterDates());

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [fromRow, setFromRow] = useState(0);
  const [toRow, setToRow] = useState(0);
  const [backendTotals, setBackendTotals] = useState({
    stockInitial: 0,
    qteAppro: 0,
    qteVendue: 0,
    stockRestant: 0,
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const periodParams = periodToStockParams(
        appliedFilters.period,
        appliedFilters.dateFrom,
        appliedFilters.dateTo
      );
      const response = await axios.get(`${APP_API_URL}/stocks`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          perPage: PER_PAGE,
          searchTerm: searchTerm || undefined,
          sortKey: sortConfig.key,
          sortDirection: sortConfig.direction === 'ascending' ? 'asc' : 'desc',
          ...periodParams,
        },
      });

      setItems(response.data.data || []);
      const total = response.data.meta?.total ?? 0;
      const pg = response.data.meta?.current_page ?? 1;
      const pp = response.data.meta?.per_page ?? PER_PAGE;
      setTotalPages(response.data.meta?.last_page || 1);
      setTotalItems(total);
      setFromRow(total ? (pg - 1) * pp + 1 : 0);
      setToRow(Math.min(pg * pp, total));
      setBackendTotals(
        response.data.totals || {
          stockInitial: 0,
          qteAppro: 0,
          qteVendue: 0,
          stockRestant: 0,
        }
      );
    } catch (error) {
      console.error('Erreur stock :', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.message || 'Impossible de charger les stocks.',
      });
      setItems([]);
      setTotalPages(1);
      setTotalItems(0);
      setFromRow(0);
      setToRow(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm, sortConfig, appliedFilters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePeriodSelect = (option) => {
    setSelectedPeriod(option);
    setAppliedFilters((prev) => ({
      ...prev,
      period: option.value,
      dateFrom: '',
      dateTo: '',
    }));
    setDraftFilters({ dateFrom: '', dateTo: '' });
    setCurrentPage(1);
  };

  const handleApplyCustomDates = (e) => {
    e.preventDefault();
    setSelectedPeriod({ label: 'Personnalisé', value: '' });
    setAppliedFilters((prev) => ({
      ...prev,
      period: '',
      dateFrom: draftFilters.dateFrom,
      dateTo: draftFilters.dateTo,
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedPeriod({ label: 'Personnalisé', value: '' });
    setAppliedFilters({ period: '', ...defaultFilterDates() });
    setDraftFilters(defaultFilterDates());
    setCurrentPage(1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending',
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const exportRows = () =>
    items.map((item) => [
      item.product_name || item.designation,
      formatCodeLabel(item),
      formatSoldQuantityLabel(item.stockInitial, item),
      formatSoldQuantityLabel(item.qteAppro, item),
      formatSoldQuantityLabel(item.qteVendue, item),
      formatSoldQuantityLabel(item.stockRestant, item),
    ]);

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text(`Rapport Stock - ${APP_NAME}`, 14, 16);
    doc.autoTable({
      head: [['Produit', 'Code', 'Stock initial', 'Approvisionné', 'Vendu', 'Stock restant']],
      body: exportRows(),
      startY: 24,
      headStyles: { fillColor: [88, 204, 2] },
      styles: { fontSize: 9 },
    });
    doc.save(`Stock ${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    const worksheetData = items.map((item) => ({
      Produit: item.product_name || item.designation,
      Code: formatCodeLabel(item),
      'Stock initial': formatSoldQuantityLabel(item.stockInitial, item),
      Approvisionné: formatSoldQuantityLabel(item.qteAppro, item),
      Vendu: formatSoldQuantityLabel(item.qteVendue, item),
      'Stock restant': formatSoldQuantityLabel(item.stockRestant, item),
    }));
    const ws = XLSX.utils.json_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock');
    XLSX.writeFile(wb, `Stock ${APP_NAME}.xlsx`);
  };

  const activePeriodLabel = appliedFilters.period
    ? periodOptions.find((p) => p.value === appliedFilters.period)?.label
    : appliedFilters.dateFrom || appliedFilters.dateTo
      ? `${appliedFilters.dateFrom || '…'} → ${appliedFilters.dateTo || '…'}`
      : 'Ce mois';

  return (
    <ComptableLayout loading={isLoading}>
      <div className="duo-hero duo-hero--compact mb-3">
        <p className="duo-hero-sub mb-1">Comptabilité</p>
        <h1 className="duo-hero-title">Gestion des stocks</h1>
        <p className="duo-hero-sub mt-2 mb-0">
          Soldes par période (approvisionnements terminés et ventes approuvées ou livrées).
        </p>
      </div>

      <div className="duo-kpi-row mb-3">
        <div className="duo-kpi-chip">
          <p className="duo-kpi-chip__label">Stock initial</p>
          <p className="duo-kpi-chip__value">{formatNumber(backendTotals.stockInitial)}</p>
        </div>
        <div className="duo-kpi-chip">
          <p className="duo-kpi-chip__label">Approvisionné</p>
          <p className="duo-kpi-chip__value">{formatNumber(backendTotals.qteAppro)}</p>
        </div>
        <div className="duo-kpi-chip">
          <p className="duo-kpi-chip__label">Vendu</p>
          <p className="duo-kpi-chip__value">{formatNumber(backendTotals.qteVendue)}</p>
        </div>
        <div className="duo-kpi-chip">
          <p className="duo-kpi-chip__label">Stock restant</p>
          <p className="duo-kpi-chip__value">{formatNumber(backendTotals.stockRestant)}</p>
        </div>
      </div>

      <div className="mb-3">
        <DuoPeriodPicker
          options={periodOptions}
          selectedOption={selectedPeriod}
          onSelect={handlePeriodSelect}
        />
        <p className="small text-muted mt-2 mb-0">
          Période active : <strong>{activePeriodLabel}</strong>
        </p>
      </div>

      <section className="duo-panel">
        <div className="duo-toolbar">
          <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
            Détail par produit
          </span>
          <div className="duo-toolbar__actions">
            <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
              <i className="bi bi-filetype-pdf" />
            </button>
            <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
              <i className="bi bi-filetype-xls" />
            </button>
            <button type="button" title="Rafraîchir" onClick={fetchData} className="duo-btn duo-btn--ghost duo-btn--icon">
              <i className="bi bi-arrow-clockwise" />
            </button>
            <button
              type="button"
              title="Filtres"
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${filtersOpen ? ' is-open' : ''}`}
            >
              <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
            </button>
          </div>
        </div>

        <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
          <input
            type="search"
            className="duo-input"
            placeholder="Rechercher par référence ou nom produit..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        {filtersOpen && (
          <div className="duo-filters">
            <form onSubmit={handleApplyCustomDates}>
              <p className="small text-muted mb-2">Plage personnalisée (remplace la période rapide)</p>
              <div className="duo-filters__grid">
                <div>
                  <label className="duo-form-label">Date de début</label>
                  <input
                    type="date"
                    className="duo-input"
                    value={draftFilters.dateFrom}
                    onChange={(e) => setDraftFilters((p) => ({ ...p, dateFrom: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="duo-form-label">Date de fin</label>
                  <input
                    type="date"
                    className="duo-input"
                    value={draftFilters.dateTo}
                    onChange={(e) => setDraftFilters((p) => ({ ...p, dateTo: e.target.value }))}
                  />
                </div>
              </div>
              <div className="d-flex justify-content-end gap-2">
                <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                  Réinitialiser
                </button>
                <button type="submit" className="duo-btn duo-btn--primary">
                  <i className="bi bi-search" /> Appliquer
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="duo-table-wrap">
          <table className="duo-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('product_name')}>
                  Produit <SortIcon active={sortConfig.key === 'product_name'} direction={sortConfig.direction} />
                </th>
                <th>Code</th>
                <th className="text-end" onClick={() => requestSort('stockInitial')}>
                  Stock initial <SortIcon active={sortConfig.key === 'stockInitial'} direction={sortConfig.direction} />
                </th>
                <th className="text-end" onClick={() => requestSort('qteAppro')}>
                  Approvisionné <SortIcon active={sortConfig.key === 'qteAppro'} direction={sortConfig.direction} />
                </th>
                <th className="text-end" onClick={() => requestSort('qteVendue')}>
                  Vendu <SortIcon active={sortConfig.key === 'qteVendue'} direction={sortConfig.direction} />
                </th>
                <th className="text-end" onClick={() => requestSort('stockRestant')}>
                  Stock restant <SortIcon active={sortConfig.key === 'stockRestant'} direction={sortConfig.direction} />
                </th>
              </tr>
            </thead>
            <tbody>
              {items.length > 0 ? (
                items.map((item) => (
                  <tr key={item.variant_id ?? item.sku}>
                    <td className="fw-semibold">{item.product_name || item.designation}</td>
                    <td>{formatCodeLabel(item)}</td>
                    <td>
                      <StockQtyCell value={item.stockInitial} row={item} />
                    </td>
                    <td>
                      <StockQtyCell value={item.qteAppro} row={item} />
                    </td>
                    <td>
                      <StockQtyCell value={item.qteVendue} row={item} />
                    </td>
                    <td>
                      <StockQtyCell value={item.stockRestant} row={item} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="duo-empty-row">
                    Aucun produit pour cette période.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalItems > 0 && (
          <div className="duo-footer-bar">
            <span>
              {fromRow} – {toRow} sur {totalItems} produit{totalItems > 1 ? 's' : ''}
            </span>
            <ul className="duo-pagination">
              <li>
                <button
                  type="button"
                  className="duo-btn duo-btn--ghost"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange('prev')}
                >
                  Précédent
                </button>
              </li>
              <li>
                <span className="duo-pagination__info px-2">
                  Page {currentPage} / {totalPages}
                </span>
              </li>
              <li>
                <button
                  type="button"
                  className="duo-btn duo-btn--primary"
                  disabled={currentPage >= totalPages}
                  onClick={() => handlePageChange('next')}
                >
                  Suivant
                </button>
              </li>
            </ul>
          </div>
        )}
      </section>
    </ComptableLayout>
  );
}
